/**
 * "Send to a 360 Tour" picker for the standalone staging flow.
 *
 * Shown from the ResultViewer when the user staged a panorama directly in
 * the staging app (no `returnTo=tours` URL params) and clicks "Open in
 * 360 Tours". Lets the user pick which tour + scene to attach the staged
 * image to as the staged variant — instead of dumping them on the tours
 * dashboard with no context.
 *
 * Flow:
 *   1. On open, query the user's tours and their scenes.
 *   2. User picks a tour, then a scene within it.
 *   3. We INSERT into `staged_returns` (claimed_at NULL). Tours' on-load
 *      handler `claimStagingReturnsForTour` picks it up next time the
 *      user opens that tour and applies the staged variant.
 *   4. We optionally open the destination tour in a new tab so the user
 *      sees the result immediately.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Check, Image as ImageIcon, Layers, Loader2, RefreshCw, X } from 'lucide-react';
import { supabase, isConfigured } from '../services/supabase';

/** How the staged image should land on the destination scene. */
type AttachMode = 'attach' | 'replace';

interface SendToTourModalProps {
    userId: string;
    /** Public URL of the staged equirect to attach. */
    stagedImageUrl: string;
    /** Tours app base URL — opened in a new tab on success so the user lands on the destination tour. */
    toursAppUrl: string;
    /**
     * When true the picker lists EVERY tour on the platform (not just the
     * signed-in user's) and writes the staged_returns row owned by the tour
     * owner — so the customer's Tours app claims it. Used for done-for-you
     * (managed-service) virtual staging where an admin stages a customer's
     * panorama and sends it back to their tour.
     */
    isAdmin?: boolean;
    onClose: () => void;
}

interface TourRow {
    id: string;
    title: string;
    slug: string | null;
    /** Tour owner. In admin mode the staged_returns row is owned by this id. */
    user_id: string;
    /** Owner email, resolved for display in admin mode only. */
    owner_email?: string | null;
    scene_count: number;
}

interface SceneRow {
    id: string;
    tour_id: string;
    title: string;
    image_url: string;
    thumbnail_url: string | null;
    /** From the join — picked so we can show "already has a staged variant" hints. */
    staged_status: string | null;
}

const SendToTourModal: React.FC<SendToTourModalProps> = ({ userId, stagedImageUrl, toursAppUrl, isAdmin = false, onClose }) => {
    const [tours, setTours] = useState<TourRow[]>([]);
    const [scenes, setScenes] = useState<SceneRow[]>([]);
    /** Admin-only free-text search across all tours (title or owner email). */
    const [search, setSearch] = useState('');
    const [loadingTours, setLoadingTours] = useState(true);
    const [loadingScenes, setLoadingScenes] = useState(false);
    const [selectedTourId, setSelectedTourId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<string | null>(null); // sceneId being submitted
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [doneSceneId, setDoneSceneId] = useState<string | null>(null);
    /**
     * Attach = keep both (Tours' viewer toggles between original and staged).
     * Replace = overwrite the scene's primary image_url with the staged one.
     */
    const [mode, setMode] = useState<AttachMode>('attach');
    /**
     * User-supplied name for this staged variant. Stored on the
     * scene_staged_variants row as `label` and surfaced in the viewer
     * toggle. Defaults to "Staged" — for second / third variants the
     * user is encouraged to pick something distinctive ("Modern",
     * "Scandi", etc.).
     */
    const [variantLabel, setVariantLabel] = useState<string>('Staged');

    // Load tours. Non-admins see only their own tours. Admins see every tour
    // on the platform (RLS "Admins can view all tours") and can search by tour
    // title or owner email — required for done-for-you staging where the
    // destination tour belongs to a customer, not the admin.
    useEffect(() => {
        if (!isConfigured || !userId) {
            setLoadingTours(false);
            return;
        }
        let cancelled = false;
        setLoadingTours(true);
        // Debounce admin search so we don't query on every keystroke.
        const delay = isAdmin && search.trim() ? 250 : 0;
        const timer = setTimeout(() => { void load(); }, delay);

        async function load() {
            try {
                // PostgREST `.or()` treats commas/parentheses as syntax, so
                // strip them from the user-supplied search before interpolating.
                const term = search.trim().replace(/[(),]/g, ' ').trim();

                // In admin mode, also resolve owner ids whose email matches the
                // search so "find by customer email" works.
                let ownerIdsFromEmail: string[] = [];
                if (isAdmin && term) {
                    const { data: profs } = await supabase
                        .from('profiles')
                        .select('id')
                        .ilike('email', `%${term}%`)
                        .limit(20);
                    ownerIdsFromEmail = (profs ?? []).map((p: any) => p.id).filter(Boolean);
                }

                let query = supabase
                    .from('tours')
                    .select('id, title, slug, user_id, scenes(id)')
                    .order('updated_at', { ascending: false })
                    .limit(50);

                if (!isAdmin) {
                    query = query.eq('user_id', userId);
                } else if (term) {
                    const ors = [`title.ilike.%${term}%`];
                    if (ownerIdsFromEmail.length) {
                        ors.push(`user_id.in.(${ownerIdsFromEmail.join(',')})`);
                    }
                    query = query.or(ors.join(','));
                }

                const { data, error } = await query;
                if (cancelled) return;
                if (error) {
                    setErrorMessage(error.message);
                    setTours([]);
                    return;
                }

                const rows = (data ?? []) as any[];

                // Resolve owner emails for display (admin mode only).
                const emailMap = new Map<string, string>();
                if (isAdmin && rows.length) {
                    const ids = Array.from(new Set(rows.map((r) => r.user_id))).filter(Boolean);
                    if (ids.length) {
                        const { data: profs } = await supabase
                            .from('profiles')
                            .select('id, email')
                            .in('id', ids);
                        for (const p of (profs ?? []) as any[]) {
                            if (p?.id) emailMap.set(p.id, p.email);
                        }
                    }
                }

                if (cancelled) return;
                const mapped: TourRow[] = rows.map((t) => ({
                    id: t.id,
                    title: t.title,
                    slug: t.slug,
                    user_id: t.user_id,
                    owner_email: isAdmin ? (emailMap.get(t.user_id) ?? null) : null,
                    scene_count: Array.isArray(t.scenes) ? t.scenes.length : 0,
                }));
                setTours(mapped);
            } catch (err) {
                if (!cancelled) setErrorMessage(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoadingTours(false);
            }
        }

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [userId, isAdmin, search]);

    // Load scenes for the selected tour
    useEffect(() => {
        if (!selectedTourId) {
            setScenes([]);
            return;
        }
        let cancelled = false;
        setLoadingScenes(true);
        (async () => {
            try {
                const { data, error } = await supabase
                    .from('scenes')
                    .select('id, tour_id, title, image_url, thumbnail_url, staged_status')
                    .eq('tour_id', selectedTourId)
                    .order('order', { ascending: true });
                if (cancelled) return;
                if (error) {
                    setErrorMessage(error.message);
                    setScenes([]);
                    return;
                }
                setScenes((data ?? []) as SceneRow[]);
            } catch (err) {
                if (!cancelled) setErrorMessage(err instanceof Error ? err.message : String(err));
            } finally {
                if (!cancelled) setLoadingScenes(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedTourId]);

    const selectedTour = useMemo(
        () => tours.find((t) => t.id === selectedTourId) ?? null,
        [tours, selectedTourId],
    );

    const handleAttach = async (scene: SceneRow) => {
        if (!isConfigured || !userId || submitting) return;
        if (mode === 'replace') {
            const ok = window.confirm(
                `Replace the original image of "${scene.title}" with this staged version?\n\n` +
                `The original empty-room pano will no longer be visible in the tour or in the staged/original toggle.`,
            );
            if (!ok) return;
        }
        setSubmitting(scene.id);
        setErrorMessage(null);
        try {
            const finalLabel = (variantLabel || '').trim() || 'Staged';
            // The Tours app claims a staged_returns row only when its user_id
            // matches the person opening the tour. So the row must be owned by
            // the TOUR OWNER. For the normal self-serve flow that's the signed-in
            // user; in admin (done-for-you) mode it's the customer who owns the
            // destination tour. (RLS allows admins to insert rows for any user.)
            const ownerId = isAdmin ? (selectedTour?.user_id ?? userId) : userId;
            const { error } = await supabase
                .from('staged_returns')
                .insert({
                    user_id: ownerId,
                    tour_id: scene.tour_id,
                    scene_id: scene.id,
                    staged_image_url: stagedImageUrl,
                    mode,
                    variant_label: finalLabel,
                    staged_meta: {
                        version: 1,
                        source: isAdmin ? 'staging-app-admin' : 'staging-app-standalone',
                        attach_mode: mode,
                        label: finalLabel,
                    },
                });
            if (error) {
                setErrorMessage(error.message);
                setSubmitting(null);
                return;
            }
            setDoneSceneId(scene.id);
            // Open the destination tour so the user sees the apply happen.
            // The tours app's on-load `claimStagingReturnsForTour` picks up
            // the row we just inserted and pops the result viewer.
            // Skip in admin mode: the tour belongs to the customer, so the
            // claim runs in THEIR session (not the admin's) — opening it here
            // would just show the unchanged tour.
            if (selectedTour && !isAdmin) {
                const tourPath = selectedTour.slug ? `/tour/${selectedTour.slug}` : `/?tour=${selectedTour.id}`;
                try {
                    window.open(`${toursAppUrl.replace(/\/$/, '')}${tourPath}`, '_blank');
                } catch {
                    // best-effort
                }
            }
        } catch (err) {
            setErrorMessage(err instanceof Error ? err.message : String(err));
            setSubmitting(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
            <div className="relative flex h-[min(720px,90vh)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 shadow-2xl">
                {/* Header */}
                <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-800 px-5 py-4">
                    <div>
                        <h2 className="text-base font-semibold text-slate-100">
                            {isAdmin ? 'Send staged 360° to any tour' : 'Send staged 360° to a tour'}
                        </h2>
                        <p className="mt-0.5 text-xs text-slate-400">
                            {isAdmin
                                ? 'Admin: pick any customer\u2019s tour and scene. The staged version is attached to their tour and appears for them next time they open it. The original pano stays untouched.'
                                : 'Pick a tour and the scene to attach this staged version to. The original pano stays untouched — viewers can toggle between them in your tour.'}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Body: two-pane layout */}
                <div className="flex flex-1 min-h-0">
                    {/* Tours pane */}
                    <div className="flex w-1/2 flex-col border-r border-slate-800">
                        <div className="flex-shrink-0 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            {isAdmin ? 'All tours' : 'Your tours'}
                        </div>
                        {isAdmin && (
                            <div className="flex-shrink-0 px-3 pb-2">
                                <input
                                    type="text"
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="Search by tour title or customer email"
                                    className="w-full rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-orange-500 focus:outline-none"
                                />
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto px-2 pb-3">
                            {loadingTours ? (
                                <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-400">
                                    <Loader2 size={14} className="animate-spin" />
                                    Loading tours&hellip;
                                </div>
                            ) : tours.length === 0 ? (
                                <p className="px-3 py-6 text-sm text-slate-500">
                                    {isAdmin
                                        ? (search.trim()
                                            ? 'No tours match that search.'
                                            : 'No tours found.')
                                        : 'No tours yet. Create one in 360 Tours first, then come back.'}
                                </p>
                            ) : (
                                tours.map((tour) => (
                                    <button
                                        key={tour.id}
                                        onClick={() => setSelectedTourId(tour.id)}
                                        className={`mb-1 flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left transition-colors ${
                                            selectedTourId === tour.id
                                                ? 'bg-orange-600/15 ring-1 ring-orange-500/40'
                                                : 'hover:bg-slate-900'
                                        }`}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <p className={`truncate text-sm font-medium ${selectedTourId === tour.id ? 'text-orange-200' : 'text-slate-200'}`}>
                                                {tour.title || 'Untitled tour'}
                                            </p>
                                            {isAdmin && tour.owner_email && (
                                                <p className="truncate text-[11px] text-slate-400">
                                                    {tour.owner_email}
                                                </p>
                                            )}
                                            <p className="text-xs text-slate-500">
                                                {tour.scene_count} {tour.scene_count === 1 ? 'scene' : 'scenes'}
                                            </p>
                                        </div>
                                        {selectedTourId === tour.id && (
                                            <ArrowRight size={14} className="flex-shrink-0 text-orange-400" />
                                        )}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Scenes pane */}
                    <div className="flex w-1/2 flex-col">
                        <div className="flex-shrink-0 px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                            {selectedTour ? `Scenes in "${selectedTour.title}"` : 'Pick a tour first'}
                        </div>

                        {selectedTour && (
                            <div className="flex-shrink-0 space-y-2.5 px-4 pb-3">
                                <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-800 bg-slate-900 p-1">
                                    <button
                                        onClick={() => setMode('attach')}
                                        className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                                            mode === 'attach'
                                                ? 'bg-orange-600 text-white'
                                                : 'text-slate-300 hover:bg-slate-800'
                                        }`}
                                        title="Keep both — original stays, staged version added as a toggle in the viewer"
                                    >
                                        <Layers size={12} />
                                        Attach as variant
                                    </button>
                                    <button
                                        onClick={() => setMode('replace')}
                                        className={`flex items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${
                                            mode === 'replace'
                                                ? 'bg-orange-600 text-white'
                                                : 'text-slate-300 hover:bg-slate-800'
                                        }`}
                                        title="Overwrite the scene's primary image with the staged version"
                                    >
                                        <RefreshCw size={12} />
                                        Replace original
                                    </button>
                                </div>

                                {mode === 'attach' && (
                                    <label className="flex flex-col gap-1 text-xs">
                                        <span className="font-medium text-slate-300">Name this version</span>
                                        <input
                                            type="text"
                                            value={variantLabel}
                                            onChange={(e) => setVariantLabel(e.target.value)}
                                            maxLength={40}
                                            placeholder="e.g. Modern, Scandi, Family-friendly"
                                            className="rounded-md border border-slate-700 bg-slate-900 px-2.5 py-1.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-orange-500 focus:outline-none"
                                        />
                                        <span className="text-[10px] text-slate-500">
                                            Shown in the viewer toggle. Pick something distinctive when you add a second / third
                                            variant to the same pano.
                                        </span>
                                    </label>
                                )}

                                <p className="text-[10px] leading-snug text-slate-500">
                                    {mode === 'attach'
                                        ? 'Both panos kept on the scene. Audience can cycle through Original and any named variants in the viewer.'
                                        : 'Overwrites image_url and clears any existing staged variants on the scene. The empty-room original will no longer be visible.'}
                                </p>
                            </div>
                        )}
                        <div className="flex-1 overflow-y-auto px-2 pb-3">
                            {!selectedTourId ? (
                                <div className="flex h-full items-center justify-center text-center text-xs text-slate-600">
                                    <span className="max-w-[200px]">
                                        Choose a tour on the left to see its scenes.
                                    </span>
                                </div>
                            ) : loadingScenes ? (
                                <div className="flex items-center gap-2 px-3 py-6 text-sm text-slate-400">
                                    <Loader2 size={14} className="animate-spin" />
                                    Loading scenes&hellip;
                                </div>
                            ) : scenes.length === 0 ? (
                                <p className="px-3 py-6 text-sm text-slate-500">
                                    This tour has no scenes yet.
                                </p>
                            ) : (
                                scenes.map((scene) => {
                                    const isSubmitting = submitting === scene.id;
                                    const isDone = doneSceneId === scene.id;
                                    const hasExistingStaged = scene.staged_status === 'complete';
                                    return (
                                        <div
                                            key={scene.id}
                                            className={`mb-2 flex items-center gap-3 rounded-lg border border-slate-800 p-2 transition-colors ${
                                                isDone ? 'border-emerald-700/40 bg-emerald-900/15' : 'bg-slate-900/50 hover:border-slate-700'
                                            }`}
                                        >
                                            <div className="h-12 w-20 flex-shrink-0 overflow-hidden rounded bg-slate-800">
                                                {scene.thumbnail_url || scene.image_url ? (
                                                    <img
                                                        src={scene.thumbnail_url || scene.image_url}
                                                        alt={scene.title}
                                                        className="h-full w-full object-cover"
                                                    />
                                                ) : (
                                                    <div className="flex h-full w-full items-center justify-center text-slate-600">
                                                        <ImageIcon size={14} />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium text-slate-200">
                                                    {scene.title}
                                                </p>
                                                {!isDone && mode === 'attach' && hasExistingStaged && (
                                                    <p className="text-[10px] text-slate-400/90">
                                                        Already has staged versions &mdash; this adds another.
                                                    </p>
                                                )}
                                                {!isDone && mode === 'replace' && (
                                                    <p className="text-[10px] text-amber-400/80">
                                                        Will overwrite the original and remove any existing staged versions.
                                                    </p>
                                                )}
                                            </div>
                                            {isDone ? (
                                                <span className="flex flex-shrink-0 items-center gap-1 rounded-full bg-emerald-900/40 px-2 py-1 text-[11px] font-semibold text-emerald-300">
                                                    <Check size={11} />
                                                    Sent
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={() => handleAttach(scene)}
                                                    disabled={isSubmitting || !!submitting}
                                                    className="flex flex-shrink-0 items-center gap-1.5 rounded-full bg-gradient-to-r from-indigo-600 via-purple-600 to-fuchsia-500 px-3 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    {isSubmitting ? (
                                                        <Loader2 size={11} className="animate-spin" />
                                                    ) : (
                                                        <ArrowRight size={11} />
                                                    )}
                                                    {isSubmitting
                                                        ? 'Sending\u2026'
                                                        : mode === 'replace'
                                                            ? 'Replace'
                                                            : 'Attach'}
                                                </button>
                                            )}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                {errorMessage && (
                    <div className="flex-shrink-0 border-t border-slate-800 bg-red-950/40 px-5 py-2 text-xs text-red-300">
                        {errorMessage}
                    </div>
                )}
                {doneSceneId && (
                    <div className="flex-shrink-0 border-t border-slate-800 bg-emerald-950/40 px-5 py-3 text-xs text-emerald-200">
                        {isAdmin
                            ? 'Done. The staged version is queued on the customer\u2019s tour and will appear for them next time they open it.'
                            : 'Done. Opening the tour in a new tab so you can see the staged version.'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SendToTourModal;
